'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

type SignupFormData = {
  fullName: string;
  email: string;
  phone: string;
  goal: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
};

const goalOptions = [
  { value: 'professional', label: 'Improve workplace communication' },
  { value: 'interview', label: 'Crack job interviews' },
  { value: 'confidence', label: 'Build speaking confidence' },
  { value: 'daily', label: 'Speak English in daily life' },
  { value: 'academic', label: 'Academic / study purposes' },
  { value: 'other', label: 'Other' },
];

const passwordStrengthCheck = (password: string) => {
  if (!password) return { strength: 0, label: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['', 'bg-danger', 'bg-warning', 'bg-amber-400', 'bg-primary'];
  return { strength: score, label: labels[score], color: colors[score] };
};

export default function SignupForm({ onSwitchToLogin }: { onSwitchToLogin: () => void }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>();

  const passwordValue = watch('password', '');
  const { strength, label, color } = passwordStrengthCheck(passwordValue);

  // Backend integration point: replace with real registration API call
  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1500));
    toast.success(`Welcome to Fluent English Blueprint, ${data.fullName.split(' ')[0]}! 🎉`);
    setTimeout(() => router.push('/student-dashboard'), 1000);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-extrabold text-foreground mb-1">Start your journey</h1>
        <p className="text-sm text-muted-foreground">
          Create your free account and begin the 90-day transformation
        </p>
      </div>

      {/* Google auth */}
      <button
        type="button"
        className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border-2 border-border bg-card hover:bg-muted font-semibold text-sm text-foreground transition-all btn-press"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
          <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        Sign up with Google
      </button>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs font-medium text-muted-foreground">or create account with email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        {/* Full name */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="signup-name" className="text-sm font-semibold text-foreground">
            Full name
          </label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder="Priya Raghunandan"
            className={`input-field ${errors.fullName ? 'input-error' : ''}`}
            {...register('fullName', {
              required: 'Full name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' },
            })}
          />
          {errors.fullName && (
            <p className="text-xs font-medium text-danger">{errors.fullName.message}</p>
          )}
        </div>

        {/* Email */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="signup-email" className="text-sm font-semibold text-foreground">
            Email address
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`input-field ${errors.email ? 'input-error' : ''}`}
            {...register('email', {
              required: 'Email address is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email address',
              },
            })}
          />
          {errors.email && (
            <p className="text-xs font-medium text-danger">{errors.email.message}</p>
          )}
        </div>

        {/* Phone */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="signup-phone" className="text-sm font-semibold text-foreground">
            Mobile number
            <span className="text-xs font-normal text-muted-foreground ml-1">(for class reminders)</span>
          </label>
          <div className="flex gap-2">
            <div className="flex items-center px-3 py-3 rounded-xl bg-muted border border-transparent text-sm font-semibold text-muted-foreground flex-shrink-0">
              +91
            </div>
            <input
              id="signup-phone"
              type="tel"
              autoComplete="tel"
              placeholder="98765 43210"
              className={`input-field flex-1 ${errors.phone ? 'input-error' : ''}`}
              {...register('phone', {
                required: 'Mobile number is required',
                pattern: {
                  value: /^[6-9]\d{9}$/,
                  message: 'Enter a valid 10-digit Indian mobile number',
                },
              })}
            />
          </div>
          {errors.phone && (
            <p className="text-xs font-medium text-danger">{errors.phone.message}</p>
          )}
        </div>

        {/* Goal */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="signup-goal" className="text-sm font-semibold text-foreground">
            My main goal with English
          </label>
          <p className="text-xs text-muted-foreground">This helps Coach Ravi personalize your journey</p>
          <select
            id="signup-goal"
            className={`input-field ${errors.goal ? 'input-error' : ''}`}
            {...register('goal', { required: 'Please select your main goal' })}
          >
            <option value="">Select your goal...</option>
            {goalOptions.map((opt) => (
              <option key={`goal-${opt.value}`} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.goal && (
            <p className="text-xs font-medium text-danger">{errors.goal.message}</p>
          )}
        </div>

        {/* Password */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="signup-password" className="text-sm font-semibold text-foreground">
            Create password
          </label>
          <p className="text-xs text-muted-foreground">
            Minimum 8 characters with uppercase, number, and special character
          </p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a strong password"
              className={`input-field pr-12 ${errors.password ? 'input-error' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*\d)/,
                  message: 'Include at least one uppercase letter and one number',
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
          {/* Strength indicator */}
          {passwordValue && (
            <div className="flex items-center gap-2 mt-1">
              <div className="flex gap-1 flex-1">
                {[1, 2, 3, 4].map((s) => (
                  <div
                    key={`strength-${s}`}
                    className={`h-1.5 flex-1 rounded-full transition-all ${
                      s <= strength ? color : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs font-semibold text-muted-foreground w-12 text-right">
                {label}
              </span>
            </div>
          )}
          {errors.password && (
            <p className="text-xs font-medium text-danger">{errors.password.message}</p>
          )}
        </div>

        {/* Confirm password */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="signup-confirm" className="text-sm font-semibold text-foreground">
            Confirm password
          </label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repeat your password"
              className={`input-field pr-12 ${errors.confirmPassword ? 'input-error' : ''}`}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) =>
                  value === passwordValue || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-xs font-medium text-danger">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Terms */}
        <div className="flex items-start gap-2.5">
          <input
            id="agree-terms"
            type="checkbox"
            className="w-4 h-4 rounded border-border accent-primary mt-0.5"
            {...register('agreeTerms', {
              required: 'You must agree to the terms to continue',
            })}
          />
          <label htmlFor="agree-terms" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
            I agree to the{' '}
            <span className="font-semibold text-primary hover:underline cursor-pointer">
              Terms of Service
            </span>{' '}
            and{' '}
            <span className="font-semibold text-primary hover:underline cursor-pointer">
              Privacy Policy
            </span>
          </label>
        </div>
        {errors.agreeTerms && (
          <p className="text-xs font-medium text-danger -mt-2">{errors.agreeTerms.message}</p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3.5 rounded-2xl font-bold text-sm text-white btn-press disabled:opacity-60 disabled:cursor-not-allowed mt-1"
          style={{ background: 'var(--primary)' }}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={17} className="animate-spin" />
              Creating your account...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <CheckCircle size={17} />
              Start My Fluency Journey
            </span>
          )}
        </button>
      </form>

      {/* Switch to login */}
      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button
          onClick={onSwitchToLogin}
          className="font-bold text-primary hover:underline"
        >
          Sign in here
        </button>
      </p>

      {/* Free webinar CTA */}
      <div className="p-4 rounded-xl bg-success-bg border border-primary/20 text-center">
        <p className="text-xs font-bold text-primary mb-1">🎁 Free Masterclass for New Members</p>
        <p className="text-xs text-muted-foreground">
          Register and get instant access to the free "Speak Fluent English in 90 Days" masterclass
        </p>
      </div>
    </div>
  );
}